<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import yaml from 'js-yaml';

  export let content = '';

  const dispatch = createEventDispatcher();

  let parseError = null;
  let rawMode = false;
  let rawContent = content;

  let event = {};
  let conditionsRaw = '';
  let choicesRaw = '';
  let skillChecksRaw = '';

  const TRIGGER_TYPES = ['wander', 'new_day', 'both', 'crisis', 'manual', 'nightfall'];

  onMount(() => { parseContent(content); });

  function parseContent(src) {
    try {
      const parsed = yaml.load(src) || {};
      event = { ...parsed };
      conditionsRaw = parsed.conditions ? yaml.dump(parsed.conditions, { indent: 2 }).trimEnd() : '';
      if (parsed.trigger_type === 'nightfall') {
        skillChecksRaw = parsed.skill_checks ? yaml.dump(parsed.skill_checks, { indent: 2 }).trimEnd() : '';
        choicesRaw = '';
      } else {
        choicesRaw = parsed.choices ? yaml.dump(parsed.choices, { indent: 2 }).trimEnd() : '';
        skillChecksRaw = '';
      }
      parseError = null;
    } catch (e) {
      parseError = e.message;
      rawMode = true;
      rawContent = src;
    }
  }

  function serialize() {
    const out = { ...event };
    delete out.conditions;
    delete out.choices;
    delete out.skill_checks;

    try { out.conditions = conditionsRaw.trim() ? yaml.load(conditionsRaw) : undefined; } catch (_) {}
    if (event.trigger_type === 'nightfall') {
      try { out.skill_checks = skillChecksRaw.trim() ? yaml.load(skillChecksRaw) : undefined; } catch (_) {}
    } else {
      try { out.choices = choicesRaw.trim() ? yaml.load(choicesRaw) : undefined; } catch (_) {}
    }

    Object.keys(out).forEach(k => out[k] === undefined && delete out[k]);
    return yaml.dump(out, { indent: 2, lineWidth: 120 });
  }

  function emit() {
    if (rawMode) {
      dispatch('change', rawContent);
    } else {
      try { dispatch('change', serialize()); } catch (_) {}
    }
  }

  function field(key, value) {
    event[key] = value;
    event = event;
    emit();
  }

  function onRawChange() {
    dispatch('change', rawContent);
  }

  function switchToRaw() {
    try { rawContent = serialize(); } catch (_) { rawContent = content; }
    rawMode = true;
  }

  function switchToStructured() {
    parseContent(rawContent);
    if (!parseError) rawMode = false;
  }

  $: isNightfall = event.trigger_type === 'nightfall';
</script>

<div class="event-editor">
  <div class="editor-toolbar">
    <span class="editor-label">Event Editor</span>
    {#if rawMode}
      <button type="button" class="btn-mode" on:click={switchToStructured}>← Structured view</button>
    {:else}
      <button type="button" class="btn-mode" on:click={switchToRaw}>Edit raw YAML</button>
    {/if}
  </div>

  {#if parseError}
    <div class="parse-error">YAML parse error: {parseError}</div>
  {/if}

  {#if rawMode}
    <textarea
      class="raw-editor"
      bind:value={rawContent}
      on:input={onRawChange}
      spellcheck="false"
      autocomplete="off"
    ></textarea>
  {:else}
    <section class="card">
      <h3 class="card-title">Event Details</h3>

      <div class="field-grid">
        <div class="field">
          <label>ID</label>
          <input type="text" class="mono" value={event.id || ''} on:input={e => field('id', e.target.value)} />
        </div>
        <div class="field">
          <label>Title</label>
          <input type="text" value={event.title || ''} on:input={e => field('title', e.target.value)} />
        </div>
      </div>

      <div class="field">
        <label>Description</label>
        <textarea rows="3" value={event.description || ''} on:input={e => field('description', e.target.value)}></textarea>
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Trigger type</label>
          <select value={event.trigger_type || 'both'} on:change={e => field('trigger_type', e.target.value)}>
            {#each TRIGGER_TYPES as t}
              <option value={t}>{t}</option>
            {/each}
          </select>
        </div>
        <div class="field">
          <label>Weight <span class="hint">(relative, no max)</span></label>
          <input type="number" min="0" step="0.05" value={event.probability ?? 1.0} on:input={e => field('probability', parseFloat(e.target.value))} />
        </div>
        <div class="field">
          <label>Cooldown (days)</label>
          <input type="number" min="0" value={event.cooldown ?? ''} on:input={e => field('cooldown', e.target.value ? parseInt(e.target.value) : undefined)} />
        </div>
      </div>

      <div class="field-row">
        <label class="check-label">
          <input type="checkbox" checked={event.enabled !== false} on:change={e => field('enabled', e.target.checked)} />
          Enabled
        </label>
        <label class="check-label">
          <input type="checkbox" checked={!!event.auto_trigger} on:change={e => field('auto_trigger', e.target.checked)} />
          Auto-trigger
        </label>
        {#if isNightfall}
          <label class="check-label">
            <input type="checkbox" checked={!!event.difficulty_scaling} on:change={e => field('difficulty_scaling', e.target.checked)} />
            Difficulty scaling
          </label>
        {/if}
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Tags <span class="hint">(comma-separated)</span></label>
          <input type="text" value={Array.isArray(event.tags) ? event.tags.join(', ') : (event.tags || '')} on:input={e => field('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
        </div>
        <div class="field">
          <label>Category</label>
          <input type="text" value={event.category || ''} on:input={e => field('category', e.target.value || undefined)} />
        </div>
        <div class="field">
          <label>Tone</label>
          <input type="text" value={event.tone || ''} on:input={e => field('tone', e.target.value || undefined)} />
        </div>
      </div>

      {#if isNightfall}
        <div class="field-grid">
          <div class="field">
            <label>Day requirement</label>
            <input type="number" min="0" value={event.day_requirement ?? 0} on:input={e => field('day_requirement', parseInt(e.target.value))} />
          </div>
        </div>
      {/if}
    </section>

    <section class="card">
      <h3 class="card-title">Conditions <span class="hint">— YAML</span></h3>
      <textarea
        class="sub-yaml"
        rows="8"
        bind:value={conditionsRaw}
        on:input={emit}
        spellcheck="false"
        placeholder="- type: day_after&#10;  value: 5"
      ></textarea>
    </section>

    {#if isNightfall}
      <section class="card">
        <h3 class="card-title">Skill Checks <span class="hint">— YAML</span></h3>
        <textarea
          class="sub-yaml"
          rows="20"
          bind:value={skillChecksRaw}
          on:input={emit}
          spellcheck="false"
          placeholder="- description: What is being attempted&#10;  skill_id: perception&#10;  person: trevor&#10;  base_difficulty: 11&#10;  outcomes: ..."
        ></textarea>
      </section>
    {:else}
      <section class="card">
        <h3 class="card-title">Choices <span class="hint">— YAML</span></h3>
        <textarea
          class="sub-yaml"
          rows="20"
          bind:value={choicesRaw}
          on:input={emit}
          spellcheck="false"
          placeholder="- text: Choice text&#10;  dialog: Result message&#10;  effects:&#10;    - type: add&#10;      resource: morale&#10;      amount: 10"
        ></textarea>
      </section>
    {/if}
  {/if}
</div>

<style>
  .event-editor {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .editor-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .editor-label {
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }

  .btn-mode {
    background: none;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-size: 0.8rem;
    padding: 0.3rem 0.75rem;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-mode:hover { background: #f8fafc; }

  .parse-error {
    background: #fef2f2;
    border: 1px solid #fca5a5;
    color: #dc2626;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  .card {
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    padding: 1.25rem;
    background: white;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  .card-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .field-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.875rem;
  }

  .field-row {
    display: flex;
    gap: 1.5rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .check-label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
  }

  label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
  }

  .hint {
    font-weight: 400;
    color: #94a3b8;
    font-size: 0.75rem;
  }

  input[type="text"],
  input[type="number"],
  select,
  textarea {
    padding: 0.4rem 0.625rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    background: white;
    color: #1e293b;
    font-family: inherit;
  }

  input:focus, select:focus, textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
  }

  .mono { font-family: 'Fira Code', monospace; }

  textarea { resize: vertical; line-height: 1.5; }

  .sub-yaml, .raw-editor {
    font-family: 'Fira Code', monospace;
    font-size: 0.8rem;
    background: #0f172a;
    color: #e2e8f0;
    border-color: #334155;
    caret-color: #93c5fd;
    width: 100%;
    box-sizing: border-box;
  }

  .raw-editor {
    min-height: 60vh;
    resize: vertical;
  }
</style>
